// Proyectos y registro de horas (objetos proyecto, faseProyecto y registroHoras de la app).
// D96: el proyecto tiene valor vendido, moneda, tarifa por hora y la oportunidad de la que salio; se divide en fases con
// horas estimadas; cada registro de horas puede ir a una fase y lo aprueba (o rechaza, con nota) un supervisor.
import { quitarConsumoDe } from './bolsas';
import { create, f, listAll, remove, update } from './rest';

export type EstadoProyecto = 'PROPUESTO' | 'ACTIVO' | 'PAUSADO' | 'TERMINADO';
export type Moneda = 'USD' | 'COP';
export type Proyecto = {
  id: string;
  nombre: string;
  estado: EstadoProyecto;
  responsableId: string | null;
  inicio: string | null;
  entrega: string | null;
  horasPresupuestadas: number | null;
  descripcion: string;
  empresaId: string | null;
  empresa: { id: string; nombre: string } | null;
  valor: number | null;
  moneda: Moneda;
  tarifaHora: number | null;
  oportunidadId: string | null;
};
type RawProyecto = {
  id: string;
  name: string;
  estado: EstadoProyecto | null;
  responsableId: string | null;
  inicio: string | null;
  entrega: string | null;
  horasPresupuestadas: number | null;
  descripcion: string | null;
  empresaId: string | null;
  empresa?: { id: string; name?: string } | null;
  valor?: number | null;
  moneda?: Moneda | null;
  tarifaHora?: number | null;
  oportunidadId?: string | null;
};
const aProyecto = (r: RawProyecto): Proyecto => ({
  id: r.id,
  nombre: r.name,
  estado: r.estado ?? 'ACTIVO',
  responsableId: r.responsableId,
  inicio: r.inicio,
  entrega: r.entrega,
  horasPresupuestadas: r.horasPresupuestadas,
  descripcion: r.descripcion ?? '',
  empresaId: r.empresaId,
  empresa: r.empresa ? { id: r.empresa.id, nombre: r.empresa.name ?? 'Empresa' } : null,
  valor: r.valor ?? null,
  moneda: r.moneda ?? 'USD',
  tarifaHora: r.tarifaHora ?? null,
  oportunidadId: r.oportunidadId ?? null,
});

export const proyectos = async () => (await listAll<RawProyecto>('proyectos', { orderBy: 'name[AscNullsLast]', depth: 1 }, 500)).map(aProyecto);

export type ProyectoInput = Omit<Proyecto, 'id' | 'empresa'>;
const cuerpoProyecto = (p: ProyectoInput) => ({
  name: p.nombre.trim(),
  estado: p.estado,
  responsableId: p.responsableId,
  inicio: p.inicio,
  entrega: p.entrega,
  horasPresupuestadas: p.horasPresupuestadas,
  descripcion: p.descripcion,
  empresaId: p.empresaId,
  valor: p.valor,
  moneda: p.moneda,
  tarifaHora: p.tarifaHora,
  oportunidadId: p.oportunidadId,
});
export const crearProyecto = (p: ProyectoInput) => create<{ id: string }>('proyectos', cuerpoProyecto(p));
export const editarProyecto = (id: string, p: ProyectoInput) => update('proyectos', id, cuerpoProyecto(p));
// Sus fases se borran con el (no son una relacion de Twenty: se guardan con el id del proyecto).
export const borrarProyecto = async (id: string) => {
  for (const x of await fases(id)) await remove('fasesProyecto', x.id);
  return remove('proyectos', id);
};

/* ---------------- fases ---------------- */

export type EstadoFase = 'PENDIENTE' | 'EN_CURSO' | 'HECHA';
export type Fase = { id: string; nombre: string; proyectoId: string | null; horasEstimadas: number | null; posicion: number; estado: EstadoFase };
type RawFase = { id: string; name: string | null; proyectoId: string | null; horasEstimadas: number | null; posicion: number | null; estado: EstadoFase | null };
const aFase = (r: RawFase): Fase => ({ id: r.id, nombre: r.name ?? 'Fase', proyectoId: r.proyectoId, horasEstimadas: r.horasEstimadas, posicion: r.posicion ?? 0, estado: r.estado ?? 'PENDIENTE' });
/** Las fases de un proyecto, o de todos (para la lista y el Resumen). */
export const fases = async (proyectoId?: string) =>
  (await listAll<RawFase>('fasesProyecto', { filter: proyectoId ? f.eq('proyectoId', proyectoId) : undefined, orderBy: 'posicion[AscNullsLast]' }, 3000)).map(aFase);
export type FaseInput = Omit<Fase, 'id'>;
const cuerpoFase = (x: Partial<FaseInput>) => ({
  ...(x.nombre !== undefined ? { name: x.nombre.trim() } : {}),
  ...(x.proyectoId !== undefined ? { proyectoId: x.proyectoId } : {}),
  ...(x.horasEstimadas !== undefined ? { horasEstimadas: x.horasEstimadas } : {}),
  ...(x.posicion !== undefined ? { posicion: x.posicion } : {}),
  ...(x.estado !== undefined ? { estado: x.estado } : {}),
});
export const crearFase = (x: FaseInput) => create<{ id: string }>('fasesProyecto', cuerpoFase(x));
export const editarFase = (id: string, x: Partial<FaseInput>) => update('fasesProyecto', id, cuerpoFase(x));
export const borrarFase = (id: string) => remove('fasesProyecto', id);

/* ---------------- horas ---------------- */

export type Aprobacion = 'PENDIENTE' | 'APROBADA' | 'RECHAZADA';
export type RegistroHoras = {
  id: string;
  descripcion: string;
  minutos: number;
  fecha: string;
  miembroId: string | null;
  facturable: boolean;
  proyectoId: string | null;
  casoId: string | null;
  faseId?: string | null;
  aprobacion?: Aprobacion;
  aprobadoPor?: string;
  aprobadoEn?: string | null;
  nota?: string;
};
type RawRegistro = {
  id: string;
  name: string;
  minutos: number | null;
  fecha: string | null;
  miembroId: string | null;
  facturable: boolean | null;
  proyectoId: string | null;
  casoId: string | null;
  createdAt: string;
  faseId?: string | null;
  aprobacion?: Aprobacion | null;
  aprobadoPor?: string | null;
  aprobadoEn?: string | null;
  nota?: string | null;
};
const aRegistro = (r: RawRegistro): RegistroHoras => ({
  id: r.id,
  descripcion: r.name ?? '',
  minutos: r.minutos ?? 0,
  fecha: r.fecha ?? r.createdAt,
  miembroId: r.miembroId,
  facturable: r.facturable !== false,
  proyectoId: r.proyectoId,
  casoId: r.casoId,
  faseId: r.faseId ?? null,
  aprobacion: r.aprobacion ?? 'PENDIENTE',
  aprobadoPor: r.aprobadoPor ?? '',
  aprobadoEn: r.aprobadoEn ?? null,
  nota: r.nota ?? '',
});

export const horasEntre = async (desde: Date, hasta: Date) =>
  (await listAll<RawRegistro>('registrosHoras', { filter: f.and(f.gte('fecha', desde.toISOString()), f.lte('fecha', hasta.toISOString())), orderBy: 'fecha[DescNullsLast]' }, 5000)).map(aRegistro);

export const horasDe = async (campo: 'proyectoId' | 'casoId', id: string) =>
  (await listAll<RawRegistro>('registrosHoras', { filter: f.eq(campo, id), orderBy: 'fecha[DescNullsLast]' }, 2000)).map(aRegistro);

/** Horas de varios proyectos (lista de proyectos, Resumen, campana), de a 50 por consulta. */
export async function horasDeProyectos(ids: string[]) {
  const todas: RegistroHoras[] = [];
  for (let i = 0; i < ids.length; i += 50) todas.push(...(await listAll<RawRegistro>('registrosHoras', { filter: f.in('proyectoId', ids.slice(i, i + 50)) }, 5000)).map(aRegistro));
  return todas;
}

export type RegistroInput = Omit<RegistroHoras, 'id' | 'aprobacion' | 'aprobadoPor' | 'aprobadoEn' | 'nota'>;
export const registrarHoras = (r: RegistroInput) =>
  create<{ id: string }>('registrosHoras', {
    name: r.descripcion.trim() || 'Trabajo',
    minutos: Math.round(r.minutos),
    fecha: r.fecha,
    miembroId: r.miembroId,
    facturable: r.facturable,
    proyectoId: r.proyectoId,
    casoId: r.casoId,
    faseId: r.faseId ?? null,
  });
/** Cambia los minutos (o la descripcion) de un registro desde la hoja semanal: vuelve a quedar por aprobar. */
export const editarRegistro = (id: string, c: { minutos?: number; descripcion?: string }) =>
  update('registrosHoras', id, { ...(c.minutos !== undefined ? { minutos: Math.round(c.minutos) } : {}), ...(c.descripcion !== undefined ? { name: c.descripcion.trim() || 'Trabajo' } : {}), aprobacion: 'PENDIENTE', nota: '' });
// Su consumo de bolsa de horas (D82) se borra con el.
export const borrarRegistro = async (id: string) => {
  await quitarConsumoDe(id);
  return remove('registrosHoras', id);
};
/** Aprobar o rechazar (con nota) registros de horas. */
export async function revisarHoras(ids: string[], aprobacion: 'APROBADA' | 'RECHAZADA', quien: string, nota = '') {
  const cuando = new Date().toISOString();
  for (const id of ids) await update('registrosHoras', id, { aprobacion, aprobadoPor: quien, aprobadoEn: cuando, nota: aprobacion === 'RECHAZADA' ? nota : '' });
}

export const ESTADOS_PROYECTO: [EstadoProyecto, string, 'acc' | 'ok' | 'warn' | undefined][] = [
  ['PROPUESTO', 'Propuesto', undefined],
  ['ACTIVO', 'Activo', 'acc'],
  ['PAUSADO', 'Pausado', 'warn'],
  ['TERMINADO', 'Terminado', 'ok'],
];

// "1 h 30 min" para mostrar minutos.
export const horasTexto = (min: number) => {
  const h = Math.floor(min / 60);
  const m = Math.round(min % 60);
  return h ? `${h} h${m ? ` ${m} min` : ''}` : `${m} min`;
};

// Suma de minutos por una clave (proyecto, persona, cliente...).
export function sumarPor<K extends string | null>(registros: RegistroHoras[], clave: (r: RegistroHoras) => K) {
  const m = new Map<K, number>();
  for (const r of registros) m.set(clave(r), (m.get(clave(r)) ?? 0) + r.minutos);
  return m;
}
